/**
 * useEditableBoard — board state hook extended for edit mode.
 *
 * Wraps the read-only board with:
 * - editMode toggle
 * - commit ops via board-ops + persist to localStorage
 * - analytics emission on each committed op
 * - drag state (which tile is being dragged, ghost position)
 *
 * Immutability: every mutation goes through board-ops (which return new Board
 * objects). setState always receives a brand-new reference.
 *
 * HIGH-2 FIX: onBoardCommit now returns boolean. If it returns false, the
 * active space was switched away before the commit landed — local board state
 * is NOT advanced in that case, preventing divergence.
 */
import { useState, useCallback } from 'react';
import {
  addTile,
  removeTile,
  moveTile,
  moveTiles,
  resizeTile,
  configureTile,
  type Board,
  type TileSize,
} from 'briefpane/schema';
import type { Tile } from 'briefpane/schema';
import type { AnalyticsEmitter } from 'briefpane/contracts';
import { buildEnvelope } from '../analytics/identity.js';
import { isActivated, markActivated, msSincePageLoad } from '../analytics/activation.js';

/* -------------------------------------------------------------------------- */
/* Session-scoped customization_ops counter (metrics-plan §1.5)               */
/* -------------------------------------------------------------------------- */

const CUSTOMIZATION_OPS_KEY = 'snip_customization_ops';

/** Read the running customization_ops count from sessionStorage. */
export function getCustomizationOps(): number {
  try {
    return parseInt(sessionStorage.getItem(CUSTOMIZATION_OPS_KEY) ?? '0', 10) || 0;
  } catch { return 0; }
}

/** Increment the customization_ops count and return the new value. */
function incrementCustomizationOps(): number {
  const next = getCustomizationOps() + 1;
  try { sessionStorage.setItem(CUSTOMIZATION_OPS_KEY, String(next)); } catch { /* ignore */ }
  return next;
}

/* -------------------------------------------------------------------------- */

export interface DragState {
  readonly tileId: string;
  readonly ghostX: number;
  readonly ghostY: number;
}

export type CommitError =
  | 'id-collision'
  | 'max-tiles'
  | 'unknown-tile'
  | 'invalid-size'
  | 'invalid-board'
  | null;

export interface EditableBoardState {
  readonly board: Board;
  readonly editMode: boolean;
  readonly drag: DragState | null;
  readonly lastError: CommitError;
  readonly toggleEditMode: () => void;
  readonly commitAdd: (tile: Tile, x: number, y: number) => CommitError;
  readonly commitRemove: (tileId: string) => CommitError;
  readonly commitMove: (tileId: string, x: number, y: number) => CommitError;
  readonly commitResize: (tileId: string, size: TileSize) => CommitError;
  /**
   * Commit a params update for a tile (D-011 sports team picker and future param editors).
   * Routes through `configureTile` (board-ops.ts) and emits `tile_configured` + activation.
   *
   * @param tileId      - the tile to configure
   * @param newParams   - partial params (merged onto existing params by configureTile)
   * @param settingKey  - analytics `setting_changed` enum value (defaults to 'other')
   */
  readonly commitConfigure: (
    tileId: string,
    newParams: Record<string, unknown>,
    settingKey?: 'sports_team' | 'watchlist_symbols' | 'news_topic' | 'location' | 'other',
  ) => CommitError;
  /**
   * Atomically commit a multi-tile move (dragged tile + push-aside neighbors).
   * Use when computeDisplacements returns a non-empty map so the committed layout
   * exactly matches the push-aside preview. Falls back to commitMove on failure.
   *
   * @param dragTileId - the tile the user dragged
   * @param moves      - dragged tile + all displaced neighbors with their new positions
   */
  readonly commitMoveTiles: (
    dragTileId: string,
    moves: ReadonlyArray<{ readonly tileId: string; readonly position: { readonly x: number; readonly y: number } }>,
  ) => CommitError;
  readonly setDrag: (drag: DragState | null) => void;
}

// No-op default persist (overridden by the spaces bridge in App.tsx).
// HIGH-2: returns true (success) by default so isolation tests pass.
function noopPersist(_board: Board): boolean { return true; }

/** Safely emit one analytics event; never throws. */
function safeEmit(emitter: AnalyticsEmitter, event: Parameters<AnalyticsEmitter['emit']>[0]): void {
  if (!emitter.enabled) return;
  try { emitter.emit(event); } catch { /* swallow — analytics must never degrade the product */ }
}

/**
 * Attempt to emit the board_customized activation event (fires at most once per
 * anon_id). Must be called AFTER the commit event is emitted, with the trigger op.
 */
function maybeEmitActivation(
  emitter: AnalyticsEmitter,
  triggerOp: 'tile_added' | 'tile_removed' | 'tile_moved' | 'tile_resized' | 'tile_configured',
  tileCount: number,
): void {
  if (!emitter.enabled) return;
  if (isActivated()) return;
  markActivated();
  safeEmit(emitter, {
    event: 'board_customized',
    trigger_op: triggerOp,
    ms_since_page_load: msSincePageLoad(),
    tile_count: tileCount,
    ...buildEnvelope(),
  });
}

export function useEditableBoard(
  initialBoard: Board,
  analytics: AnalyticsEmitter,
  /**
   * Persist callback injected by the spaces layer (App.tsx passes
   * `actions.updateActiveBoard`). Falls back to a no-op so the hook is usable in
   * isolation during unit tests.
   *
   * HIGH-2: returns boolean — false means the active space was switched away before
   * this commit landed. Commit callbacks check the return value and do NOT advance
   * local board state if false (prevents state divergence on rapid space switches).
   */
  onBoardCommit: (board: Board) => boolean = noopPersist,
): EditableBoardState {
  const [board, setBoard] = useState<Board>(initialBoard);
  const [editMode, setEditMode] = useState(false);
  const [drag, setDragState] = useState<DragState | null>(null);
  const [lastError, setLastError] = useState<CommitError>(null);

  const toggleEditMode = useCallback(() => {
    setEditMode((prev) => {
      if (!prev) {
        // Opening edit mode
        safeEmit(analytics, {
          event: 'edit_mode_opened',
          board_id: board.id,
          ...buildEnvelope(),
        });
      }
      return !prev;
    });
    setLastError(null);
  }, [analytics, board.id]);

  const commitAdd = useCallback(
    (tile: Tile, x: number, y: number): CommitError => {
      const result = addTile(board, tile, { x, y });
      if (!result.ok) {
        setLastError(result.error);
        return result.error;
      }
      // HIGH-2: call onBoardCommit FIRST. Only advance local state if it succeeds.
      const persisted = onBoardCommit(result.board);
      if (!persisted) {
        setLastError('invalid-board');
        return 'invalid-board';
      }
      incrementCustomizationOps();
      setBoard(result.board);
      setLastError(null);
      safeEmit(analytics, {
        event: 'tile_added',
        tile_type: tile.type,
        add_method: 'picker',
        board_id: result.board.id,
        tile_count_after: result.board.tiles.length,
        ...buildEnvelope(),
      });
      maybeEmitActivation(analytics, 'tile_added', result.board.tiles.length);
      return null;
    },
    [board, analytics, onBoardCommit],
  );

  const commitRemove = useCallback(
    (tileId: string): CommitError => {
      const tile = board.tiles.find((t) => t.id === tileId);
      const result = removeTile(board, tileId);
      if (!result.ok) {
        setLastError(result.error);
        return result.error;
      }
      // HIGH-2: call onBoardCommit FIRST. Only advance local state if it succeeds.
      const persisted = onBoardCommit(result.board);
      if (!persisted) {
        setLastError('invalid-board');
        return 'invalid-board';
      }
      incrementCustomizationOps();
      setBoard(result.board);
      setLastError(null);
      if (tile) {
        safeEmit(analytics, {
          event: 'tile_removed',
          tile_type: tile.type,
          board_id: result.board.id,
          tile_count_after: result.board.tiles.length,
          ...buildEnvelope(),
        });
        maybeEmitActivation(analytics, 'tile_removed', result.board.tiles.length);
      }
      return null;
    },
    [board, analytics, onBoardCommit],
  );

  const commitMove = useCallback(
    (tileId: string, x: number, y: number): CommitError => {
      const fromEntry = board.layout.find((e) => e.tileId === tileId);
      const tile = board.tiles.find((t) => t.id === tileId);
      const result = moveTile(board, tileId, { x, y });
      if (!result.ok) {
        setLastError(result.error);
        return result.error;
      }
      // HIGH-2: call onBoardCommit FIRST. Only advance local state if it succeeds.
      const persisted = onBoardCommit(result.board);
      if (!persisted) {
        setLastError('invalid-board');
        return 'invalid-board';
      }
      incrementCustomizationOps();
      setBoard(result.board);
      setLastError(null);
      if (tile && fromEntry) {
        safeEmit(analytics, {
          event: 'tile_moved',
          tile_type: tile.type,
          board_id: result.board.id,
          from_slot: { x: fromEntry.x, y: fromEntry.y },
          to_slot: { x, y },
          ...buildEnvelope(),
        });
        maybeEmitActivation(analytics, 'tile_moved', result.board.tiles.length);
      }
      return null;
    },
    [board, analytics, onBoardCommit],
  );

  const commitMoveTiles = useCallback(
    (
      dragTileId: string,
      moves: ReadonlyArray<{ readonly tileId: string; readonly position: { readonly x: number; readonly y: number } }>,
    ): CommitError => {
      const fromEntry = board.layout.find((e) => e.tileId === dragTileId);
      const tile = board.tiles.find((t) => t.id === dragTileId);
      const result = moveTiles(board, moves);
      if (!result.ok) {
        setLastError(result.error);
        return result.error;
      }
      // HIGH-2: call onBoardCommit FIRST. Only advance local state if it succeeds.
      const persisted = onBoardCommit(result.board);
      if (!persisted) {
        setLastError('invalid-board');
        return 'invalid-board';
      }
      incrementCustomizationOps();
      setBoard(result.board);
      setLastError(null);
      // Emit tile_moved once — for the dragged tile only (per spec).
      if (tile && fromEntry) {
        const toPos = moves.find((m) => m.tileId === dragTileId)?.position;
        safeEmit(analytics, {
          event: 'tile_moved',
          tile_type: tile.type,
          board_id: result.board.id,
          from_slot: { x: fromEntry.x, y: fromEntry.y },
          to_slot: toPos ?? { x: fromEntry.x, y: fromEntry.y },
          ...buildEnvelope(),
        });
        maybeEmitActivation(analytics, 'tile_moved', result.board.tiles.length);
      }
      return null;
    },
    [board, analytics, onBoardCommit],
  );

  const commitResize = useCallback(
    (tileId: string, size: TileSize): CommitError => {
      const tile = board.tiles.find((t) => t.id === tileId);
      const sizeBefore = tile?.display.size;
      const result = resizeTile(board, tileId, size);
      if (!result.ok) {
        setLastError(result.error);
        return result.error;
      }
      // HIGH-2: call onBoardCommit FIRST. Only advance local state if it succeeds.
      const persisted = onBoardCommit(result.board);
      if (!persisted) {
        setLastError('invalid-board');
        return 'invalid-board';
      }
      incrementCustomizationOps();
      setBoard(result.board);
      setLastError(null);
      if (tile && sizeBefore) {
        safeEmit(analytics, {
          event: 'tile_resized',
          tile_type: tile.type,
          board_id: result.board.id,
          size_before: sizeBefore,
          size_after: size,
          ...buildEnvelope(),
        });
        maybeEmitActivation(analytics, 'tile_resized', result.board.tiles.length);
      }
      return null;
    },
    [board, analytics, onBoardCommit],
  );

  const commitConfigure = useCallback(
    (
      tileId: string,
      newParams: Record<string, unknown>,
      settingKey: 'sports_team' | 'watchlist_symbols' | 'news_topic' | 'location' | 'other' = 'other',
    ): CommitError => {
      const tile = board.tiles.find((t) => t.id === tileId);
      const result = configureTile(board, tileId, newParams);
      if (!result.ok) {
        setLastError(result.error);
        return result.error;
      }
      // HIGH-2: call onBoardCommit FIRST. Only advance local state if it succeeds.
      const persisted = onBoardCommit(result.board);
      if (!persisted) {
        setLastError('invalid-board');
        return 'invalid-board';
      }
      incrementCustomizationOps();
      setBoard(result.board);
      setLastError(null);
      if (tile) {
        safeEmit(analytics, {
          event: 'tile_configured',
          tile_type: tile.type,
          board_id: result.board.id,
          setting_changed: settingKey,
          ...buildEnvelope(),
        });
        maybeEmitActivation(analytics, 'tile_configured', result.board.tiles.length);
      }
      return null;
    },
    [board, analytics, onBoardCommit],
  );

  const setDrag = useCallback((d: DragState | null) => {
    setDragState(d);
  }, []);

  return {
    board,
    editMode,
    drag,
    lastError,
    toggleEditMode,
    commitAdd,
    commitRemove,
    commitMove,
    commitMoveTiles,
    commitResize,
    commitConfigure,
    setDrag,
  };
}
